<template>
  <div class="shopify-info">
    <validation-component
      ref="shopifyForm"
      :validation-condition="shopifyCondition"
    >
    </validation-component>
  </div>
</template>

<script>
import { ShowWarningMessage } from '@/components/modal/tips/message-tips.js';
import { REQUIRED, EMAIL } from '@/components/validation/tool/validator';
import ValidationComponent from '@/components/validation/validation.vue';
import GlobalData from '@/utils/global/index';
import { initSelectedData } from '@/utils/tools/constant';
import { checkCharLength, getFileNameFromUrl } from '@/utils/tools/utils';
import { uploadMaterialUseTemplate } from '@/views/transfer/api/contact';

export default {
  components: {
    ValidationComponent,
  },
  props: {
    info: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      shopifyCondition: [{
        key: 'appName',
        name: 'message.application.merchantName',
        type: 'input',
        defValue: '',
        isShowStar: true,
        placeholder: this.$t('message.validator.input', { select: '' }),
        rule: [REQUIRED],
        maxLength: 64,
        validator: this.validateAppName,
      }, {
        key: 'storeUrl',
        name: 'message.application.websiteUrl',
        type: 'input',
        defValue: '',
        isShowStar: true,
        placeholder: this.$t('message.validator.input', { select: '' }),
        rule: [REQUIRED],
      }, {
        key: 'paypalMerInfo',
        name: 'message.application.paypalMerInfo',
        type: 'input',
        defValue: '',
        isShowStar: true,
        placeholder: this.$t('message.validator.input', { select: '' }),
        rule: [REQUIRED],
        example: {
          url: '/static/va/paypal_merinfo.jpg',
        },
      }, {
        key: 'paypalMerAccount',
        name: 'message.application.paypalMerAccount',
        type: 'input',
        defValue: '',
        isShowStar: true,
        placeholder: this.$t('message.validator.input', { select: '' }),
        rule: [REQUIRED],
        example: {
          url: '/static/va/paypal_meraccount.jpg',
        },
      }, {
        key: 'paypalEmail',
        name: 'message.application.paypalEmail',
        type: 'input',
        defValue: '',
        isShowStar: true,
        placeholder: this.$t('message.validator.input', { select: '' }),
        rule: [REQUIRED, EMAIL],
        example: {
          url: '/static/va/paypal_email.jpg',
        },
        changeValue: (value) => {
          const v = value || '';
          this.$refs.shopifyForm.setItemValue('paypalEmail', v.toLocaleLowerCase());
        },
      }, {
        key: 'paypalUserInfo',
        name: 'message.application.paypalUserInfo',
        type: 'upload_file',
        defValue: '',
        isShowStar: true,
        rule: [REQUIRED],
        example: {
          url: '/static/va/paypal_userinfo.jpg',
        },
        uploadRange: {
          fileSize: 20, // 20M
          uploadFileList: [],
          fileTypeRule: 'image/png,image/jpeg,image/jpg',
          fileTypeRuleTips: this.$t('message.supportFileErrorTip', { type: '.png,.jpeg,.jpg,.bmp' }),
          uploadTips: this.$t('message.upload.supportFileTip', { type: '.png,.jpeg,.jpg,.bmp' }),
          hideErrorMessage: true,
          fileUploadFunc: async (file) => {
            const loading = this.$loading();
            try {
              const res = await uploadMaterialUseTemplate(file);
              loading.close();
              return { fileUrl: res };
            } catch (err) {
              loading.close();
              throw err;
            }
          },
        },
      }, {
        key: 'otherData',
        name: 'message.application.otherMaterial',
        type: 'upload_file',
        defValue: '',
        isShowStar: true,
        rule: [REQUIRED],
        example: {
          url: '/static/va/va_other_material.png',
        },
        uploadRange: {
          fileSize: 20, // 20M
          uploadFileList: [],
          fileTypeRule: 'image/png,image/jpeg,image/jpg',
          fileTypeRuleTips: this.$t('message.supportFileErrorTip', { type: '.png,.jpeg,.jpg,.bmp' }),
          uploadTips: this.$t('message.upload.supportFileTip', { type: '.png,.jpeg,.jpg,.bmp' }),
          hideErrorMessage: true,
          fileUploadFunc: async (file) => {
            const loading = this.$loading();
            try {
              const res = await uploadMaterialUseTemplate(file);
              loading.close();
              return { fileUrl: res };
            } catch (err) {
              loading.close();
              throw err;
            }
          },
        },
      }, {
        key: 'salesCategory',
        name: 'message.application.salesCategory',
        type: 'select',
        defValue: '',
        isShowStar: true,
        placeholder: this.$t('message.validator.select', { select: '' }),
        rule: [REQUIRED],
      }, {
        key: 'expectedAnnualSales',
        name: 'message.application.expectedAnnualSales',
        type: 'select',
        defValue: '',
        isShowStar: true,
        placeholder: this.$t('message.validator.select', { select: '' }),
        rule: [REQUIRED],
      }, {
        key: 'storeBelongToProve',
        name: 'message.application.ownershipFile',
        type: 'upload_file',
        defValue: '',
        isShowStar: true,
        rule: [REQUIRED],
        example: {
          url: '/static/va/paypal_ownership.jpg',
        },
        uploadRange: {
          fileSize: 20, // 20M
          uploadFileList: [],
          fileTypeRule: 'image/png,image/jpeg,image/jpg',
          fileTypeRuleTips: this.$t('message.supportFileErrorTip', { type: '.png,.jpeg,.jpg,.bmp' }),
          uploadTips: this.$t('message.upload.supportFileTip', { type: '.png,.jpeg,.jpg,.bmp' }),
          hideErrorMessage: true,
          fileUploadFunc: async (file) => {
            const loading = this.$loading();
            try {
              const res = await uploadMaterialUseTemplate(file);
              loading.close();
              return { fileUrl: res };
            } catch (err) {
              loading.close();
              throw err;
            }
          },
        },
      }],
    };
  },
  mounted() {
    this.initMethod();
  },
  methods: {
    async initMethod() {
      const dict = await GlobalData.getDictList(['SALES_CATEGORIES', 'EXPECTED_ANNUAL_SALES']);
      this.$refs.shopifyForm.setValidationSelectOptions('salesCategory', initSelectedData(dict.SALES_CATEGORIES, 'description', 'value', false));
      this.$refs.shopifyForm.setValidationSelectOptions('expectedAnnualSales', initSelectedData(dict.EXPECTED_ANNUAL_SALES, 'description', 'value', false));
      this.$refs.shopifyForm.assignmentFormValue(this.info);
      if (this.info.paypalUserInfo) {
        this.shopifyCondition[5].uploadRange.uploadFileList = [{
          url: this.info.paypalUserInfo,
          name: getFileNameFromUrl(this.info.paypalUserInfo),
        }];
      }
      if (this.info.otherData) {
        this.shopifyCondition[6].uploadRange.uploadFileList = [{
          url: this.info.otherData,
          name: getFileNameFromUrl(this.info.otherData),
        }];
      }
      if (this.info.storeBelongToProve) {
        this.shopifyCondition[9].uploadRange.uploadFileList = [{
          url: this.info.storeBelongToProve,
          name: getFileNameFromUrl(this.info.storeBelongToProve),
        }];
      }
    },
    validateAppName(rule, value, callback) {
      if (value && !checkCharLength(value, 64)) {
        callback(new Error(this.$t('message.application.appNameErrorTips')));
      }
      callback();
    },
    async getParams() {
      return new Promise((resolve, reject) => {
        this.$refs.shopifyForm.toFormValid((params) => {
          const paypalUserInfoFileList = this.$refs.shopifyForm.getValidationItem('paypalUserInfo').uploadRange.uploadFileList;
          if (!paypalUserInfoFileList.length) {
            ShowWarningMessage(this.$t('message.validator.upload', { upload: this.$t('message.application.paypalUserInfo') }));
            reject(params);
            return;
          }
          const otherMaterialFileList = this.$refs.shopifyForm.getValidationItem('otherData').uploadRange.uploadFileList;
          if (!otherMaterialFileList.length) {
            ShowWarningMessage(this.$t('message.validator.upload', { upload: this.$t('message.application.otherMaterial') }));
            reject(params);
            return;
          }
          const ownershipFileList = this.$refs.shopifyForm.getValidationItem('storeBelongToProve').uploadRange.uploadFileList;
          if (!ownershipFileList.length) {
            ShowWarningMessage(this.$t('message.validator.upload', { upload: this.$t('message.application.ownershipFile') }));
            reject(params);
            return;
          }
          const paypalUserInfo = paypalUserInfoFileList[0].url;
          const storeBelongToProve = ownershipFileList[0].url;
          const otherData = otherMaterialFileList[0].url;
          const data = {
            appName: params.appName,
            applyChannelEmail: params.applyChannelEmail,
            storeUrl: params.storeUrl,
            paypalEmail: params.paypalEmail,
            paypalMerInfo: params.paypalMerInfo,
            paypalMerAccount: params.paypalMerAccount,
            paypalUserInfo,
            storeBelongToProve,
            otherData,
            salesCategory: params.salesCategory,
            expectedAnnualSales: params.expectedAnnualSales,
          };
          resolve(data);
        }, (err) => {
          reject(err);
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.shopify-info {
  margin-bottom: -20px;
  ::v-deep .rhch-upload-container {
    width: 450px;
    min-width: auto;
    .el-upload-dragger, .rhch-file-list {
      width: 450px;
      min-width: auto;
    }

    .rhch-file-list .rhch-file-item .rhcn-filename {
      max-width: 240px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  ::v-deep .el-form-item {
    margin-bottom: 20px;
  }
}
</style>
